import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { formatVnd } from "../../shared/i18n/index.ts";
import type { ListingCard as Card } from "../../shared/types.ts";
import { EmptyState } from "../components/EmptyState.tsx";
import { ListingCard } from "../components/ListingCard.tsx";
import { api } from "../lib/api.ts";
import { syncClock } from "../lib/clock.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";
import { useSegments } from "../lib/segments.ts";

type Item = Card & { target_price: number | null; fired_at: number | null };

/** /me/watching (R-ALT-4): live cards with the own target and distance; ended dimmed. */
export function Watching() {
  const { t } = useI18n();
  const { me, loading } = useMe();
  const [items, setItems] = useState<Item[] | null>(null);

  useEffect(() => {
    if (!me) return;
    let live = true;
    api<{ items: Item[]; server_ts: number }>("/api/me/watching")
      .then((r) => {
        if (!live) return;
        syncClock(r.server_ts);
        setItems(r.items);
      })
      .catch(() => live && setItems([]));
    return () => {
      live = false;
    };
  }, [me]);

  const segments = useSegments(
    items?.filter((i) => i.segment.status === "active").map((i) => i.id) ?? [],
  );

  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me) return <Navigate to="/login?next=/me/watching" replace />;

  return (
    <div className="page">
      <h1 className="h1">{t("nav.watching")}</h1>
      {items === null ? (
        <p className="muted">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <EmptyState text={t("empty.watching")} cta={{ label: t("nav.browse"), to: "/s" }} />
      ) : (
        <div className="grid" data-testid="watching-grid">
          {items.map((i) => {
            const seg = segments[i.id] ?? i.segment;
            const ended = seg.status !== "active" && seg.status !== "scheduled";
            const pct =
              i.target_price && seg.price > 0
                ? Math.round(((seg.price - i.target_price) / seg.price) * 100)
                : null;
            return (
              <div key={i.id} className={ended ? "dimmed" : ""}>
                <ListingCard card={i} segment={seg} />
                {i.target_price ? (
                  <p className={`hint${i.fired_at ? " ok" : ""}`} data-testid="watch-target">
                    {i.fired_at
                      ? t("alert.reached.short")
                      : t("alert.target", {
                          price: formatVnd(i.target_price),
                          pct: `−${Math.max(0, pct ?? 0)}%`,
                        })}
                  </p>
                ) : (
                  <p className="hint">{t("alert.watching")}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
